import { Alert, Collapse } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { Control, useFormState, get } from 'react-hook-form';
import { FORM_ERROR } from './types';

/**
 * FormErrorProps
 * @public
 */
export type FormErrorProps = {
  control?: Control<any, any>;
  name?: string;
};

/**
 * FormError component to display general form error or error for specific field.
 * This component must be used inside Form component (context from react-hook-forms).
 *
 * @example
 * ```tsx
 * <Form onSubmit={onSubmit}>
 *   <FormError />
 *   <TextField name="name" />
 *   <FormButton type="submit">Submit</Button>
 * </Form>
 * ```
 *
 * @public
 * @param control - React Hook Form control
 * @param name - Name of the field to display error for. When not provided, will display general form error
 *
 * @see Form
 */
export const FormError: React.FC<FormErrorProps> = ({
  control,
  name = FORM_ERROR,
}) => {
  const ref = useRef<any>(undefined);
  const [open, setOpen] = useState(false);

  const state = useFormState({
    control,
  });
  const error = get(state.errors, name);

  useEffect(() => {
    if (error) {
      setOpen(true);
      if (ref?.current) {
        window.scrollTo({
          top: ref.current.offsetTop - window.innerHeight / 2,
          behavior: 'smooth',
        });
      }
    } else {
      setOpen(false);
    }
  }, [error]);

  const { message: messageFromRegister } = error || {};

  return (
    <Collapse ref={ref} in={open}>
      <Alert
        color="red"
        sx={theme => ({
          padding: theme.other.spacing(1.5, 3, 1.5, 1.5),
        })}
        withCloseButton
        onClose={() => setOpen(false)}
      >
        {messageFromRegister}
      </Alert>
    </Collapse>
  );
};
